// @ts-expect-error build-time virtual module
import { bender } from 'legacy-hubspot-bender-context';
import { gate } from 'hub-http/gates';
import { getFullUrl } from 'hubspot-url-utils';
import { findMostSpecificRoute } from 'quartz-routing/internal/routeMatchingUtils';

/**
 * Shape of the granite-config-worker response served at
 * `app.hubspot.com/__granite-config.json`. The worker reads `edge_deployer_kv`
 * and exposes a route index for the domain.
 *
 * Worker source:
 * https://github.com/HubSpotEngineering/static-app-content-worker/blob/master/granite-config-worker/src/index.ts
 */

// TODO: drop FALLBACK_REMOTES in the near future.
// These serve as a pre-load safety net before the granite-config-worker
// response arrives, and as a fallback when a service is in the granite map
// but has no pathDestinationPairs entry. When apiConfig is loaded,
// resolveGraniteRemotes() does NOT use these routes for localRouteOverrides —
// pathDestinationPairs regex matching in findOwningGraniteRemote owns routing.
const FALLBACK_REMOTES = [{
  remote: 'quartz-test-granite-remote-a',
  gate: 'Granite:TestRemoteA',
  routes: ['/remote-a/:portalId']
}, {
  remote: 'quartz-test-granite-remote-b',
  gate: 'Granite:TestRemoteB',
  routes: ['/remote-b/:portalId']
}, {
  remote: 'quartz-test-granite-remote-c',
  gate: 'Granite:TestRemoteC',
  routes: ['/remote-c/:portalId']
}, {
  remote: 'quartz-app-auth-test-ui',
  gate: 'Granite:QuartzAppAuthTestApp',
  routes: ['/quartz-app-auth-test-ui/:portalId', '/quartz-app-auth-test-ui-alt/:portalId']
}, {
  remote: 'crm-index-ui',
  gate: 'FrontendFramework:Granite:CRMApps',
  routes: [
  /**
   *  These are intentionally omitted due to potential for traffic stealing.
   *
   * Examples of this would be segments-ui, prospecting-ui
   *
   *
   * '/contacts',
   * '/contacts/:portalId',
   */
  '/contacts/:portalId/objects/:objectTypeId/views/all/:viewType',
  // viewType is list, board, report ,etc.
  '/contacts/:portalId/objects/:objectTypeId/views/:viewId/:viewType', '/contacts/:portalId/objects/:objectTypeId/restore'
  // Leave these disabled for now, lets just use a few specific routes
  // '/contacts/:portalId/objects',
  // '/contacts/:portalId/search',
  // '/contacts/:portalId/companies',
  // '/prospecting/:portalId/objects',
  // { path: '/contacts/:portalId/contacts', priority: 1 },
  // { path: '/contacts/:portalId/deals', priority: 1 },
  // { path: '/contacts/:portalId/tickets', priority: 1 },
  ]
}, {
  remote: 'crm-records-ui',
  gate: 'FrontendFramework:Granite:CRMApps',
  routes: ['/contacts/:portalId/record/:objectTypeId/:objectId'
  // leave these disabled for now and just use a few specific routes
  // '/contacts/:portalId/company',
  // '/contacts/:portalId/contact',
  // '/contacts/:portalId/deal',
  // '/contacts/:portalId/ticket',
  // '/contacts/:portalId/record/:recordId',
  // '/contacts/:portalId/quote',
  // '/contacts/:portalId/companies/:companyId',
  // { path: '/contacts/:portalId/contacts/:contactId', priority: 1 },
  // { path: '/contacts/:portalId/deals/:dealId', priority: 1 },
  // { path: '/contacts/:portalId/tickets/:ticketId', priority: 1 },
  ]
}];
const BLOCKLISTED_ROUTES = ['/contacts/:portalId/record/:objectTypeId/:objectId/history', '/contacts/:portalId/record/:objectTypeId/:objectId/properties'];
export function getBlocklistedRoutes() {
  return BLOCKLISTED_ROUTES;
}
let cachedGraniteRemotes = null;
export const currentPackageName = bender.bundlingPackage;
let apiConfig = null;
let graniteConfigPromise = null;

/**
 * Fetches the granite route index from the granite-config-worker. Memoized —
 * the first call starts the request and later calls share it. The host renderer
 * awaits this before reading routes so the redirect index is present by the
 * time the link rewriter needs it.
 */
export function loadGraniteConfig() {
  if (!graniteConfigPromise) {
    const graniteConfigUrl = `${getFullUrl('app')}/__granite-config.json`;
    graniteConfigPromise = fetch(graniteConfigUrl).then(r => r.ok ? r.json() : null).catch(() => null).then(data => {
      apiConfig = data;
      cachedGraniteRemotes = null;
      return data;
    });
  }
  return graniteConfigPromise;
}

/**
 * The path/redirect index from the loaded granite-config-worker response, or
 * null if the config has not loaded. Used by the link rewriter to mirror the
 * worker's server-side redirects during soft navigation.
 */
export function getRouteIndex() {
  if (!apiConfig) {
    return null;
  }
  return {
    pathDestinationPairs: apiConfig.pathDestinationPairs,
    redirects: apiConfig.redirects
  };
}
export function resetGraniteConfigForTesting() {
  apiConfig = null;
  graniteConfigPromise = null;
  cachedGraniteRemotes = null;
}
function gatedIn(gateStr, gates) {
  if (!gateStr) return true;
  // eslint-disable-next-line hubspot-dev/no-bare-gate-strings
  return gates.has(gate(gateStr));
}

/**
 * Returns the serviceId of the granite remote that owns `pathname`, or null
 * if no enabled, gated-in granite remote owns it.
 *
 * Mirrors the static-app-content-worker (Cloudflare) routing algorithm:
 *   1. Walk `pathDestinationPairs` in order — the granite-config-worker serves
 *      them already sorted highest-priority-first, matching how the worker
 *      evaluates nginx rules. Take the first regex match.
 *   2. If the matched serviceId is present and enabled in the `granite` map,
 *      the path is owned by that remote (gate is checked). If the serviceId is
 *      absent or disabled the path is not a granite route — return null.
 *   3. If no `pathDestinationPairs` entry matches at all (e.g. test remotes
 *      that are in the `granite` map but have no nginx route yet), fall through
 *      to FALLBACK_REMOTES template matching. Known cause: the worker strips
 *      entries for services with `internal: true` in feServiceMetadata.
 *
 * When the worker response has not loaded yet, skip to step 3.
 *
 * Used by both the link rewriter (click interception) and GraniteRuntime
 * (render-time app selection) so they always agree on route ownership.
 */
export function findOwningGraniteRemote(pathname, gates) {
  var _ungated$find$remote, _ungated$find;
  if (apiConfig !== null) {
    // --- Mirrors CF worker step 1 & 2: walk pathDestinationPairs in priority order ---
    // The granite-config-worker serves pairs sorted highest-priority-first,
    // matching exactly how the static-app-content-worker evaluates nginx rules.
    for (const pair of apiConfig.pathDestinationPairs) {
      if (!pair.serviceId) continue;
      try {
        if (!new RegExp(pair.pathMatcher).test(pathname)) continue;
      } catch (_unused) {
        continue; // invalid regex — skip
      }
      // First match wins (CF worker behavior). Check granite map to determine
      // whether this is a granite-managed service or a non-granite nginx route.
      const meta = apiConfig.granite[pair.serviceId];
      if (!(meta !== null && meta !== void 0 && meta.enabled)) return null; // non-granite or explicitly disabled — don't intercept
      if (!gatedIn(meta.gate, gates)) return null; // granite but gate not satisfied
      return pair.serviceId;
    }
    // --- Step 3: no pathDestinationPairs match ---
    // A service may be absent from pathDestinationPairs even though it appears
    // in the granite map. The known cause: the granite-config-worker strips
    // entries for services marked `internal: true` in feServiceMetadata
    // (edge-deployer). The quartz config enforces that `internal` and
    // `UNSAFE_EXPERIMENTAL_graniteOptions` are mutually exclusive precisely to
    // prevent this. Falls through to FALLBACK_REMOTES as the safety net.
  }

  // Worker response not loaded OR no pathDestinationPairs match:
  // fall back to template matching on FALLBACK_REMOTES.
  const ungated = FALLBACK_REMOTES.filter(r => gatedIn(r.gate, gates));
  const templates = ungated.flatMap(r => r.routes.map(e => typeof e === 'string' ? e : e.path));
  const matched = findMostSpecificRoute(templates, pathname);
  if (!matched) return null;
  return (_ungated$find$remote = (_ungated$find = ungated.find(r => r.routes.some(e => (typeof e === 'string' ? e : e.path) === matched))) === null || _ungated$find === void 0 ? void 0 : _ungated$find.remote) !== null && _ungated$find$remote !== void 0 ? _ungated$find$remote : null;
}
function resolveGraniteRemotes() {
  if (cachedGraniteRemotes) {
    return cachedGraniteRemotes;
  }
  if (apiConfig !== null) {
    // When the worker response is available, apiConfig.granite is authoritative.
    // Routes are intentionally empty: pathDestinationPairs regex matching in
    // findOwningGraniteRemote owns all routing, so localRouteOverrides must not
    // be populated with stale FALLBACK_REMOTES template routes that would
    // short-circuit it in getAppForPath.
    cachedGraniteRemotes = Object.entries(apiConfig.granite).filter(([, meta]) => meta.enabled).map(([serviceId, meta]) => ({
      remote: serviceId,
      gate: meta.gate,
      routes: []
    }));
    return cachedGraniteRemotes;
  }

  // Worker did not respond — use FALLBACK_REMOTES as a pre-load safety net
  // with template-based routing.
  return FALLBACK_REMOTES;
}
export function isGraniteEnabledForCurrentApp(gates) {
  const graniteRemotes = resolveGraniteRemotes();
  const currentRemote = graniteRemotes.find(r => r.remote === currentPackageName);
  if (!currentRemote) {
    return false;
  }
  if (!currentRemote.gate) {
    return true;
  }
  // eslint-disable-next-line hubspot-dev/no-bare-gate-strings
  const enabled = gates.has(gate(currentRemote.gate));
  return enabled;
}
function normalizeRouteEntry(entry) {
  var _entry$priority;
  if (typeof entry === 'string') {
    return {
      path: entry,
      priority: 0
    };
  }
  return {
    path: entry.path,
    priority: (_entry$priority = entry.priority) !== null && _entry$priority !== void 0 ? _entry$priority : 0
  };
}
export function getUngatedRemoteRoutes(gates) {
  const graniteRemotes = resolveGraniteRemotes();
  const ungatedRemotes = graniteRemotes.filter(
  // eslint-disable-next-line hubspot-dev/no-bare-gate-strings
  r => !r.gate || gates.has(gate(r.gate)));
  const seen = new Set();
  const remoteModules = [];
  const localRouteOverrides = {};
  for (const ungatedRemote of ungatedRemotes) {
    const isLocal = ungatedRemote.remote === currentPackageName;
    if (!isLocal && !seen.has(ungatedRemote.remote)) {
      seen.add(ungatedRemote.remote);
      remoteModules.push(ungatedRemote.remote);
    }
    for (const entry of ungatedRemote.routes) {
      const {
        path: routeTemplate
      } = normalizeRouteEntry(entry);
      if (isLocal) {
        localRouteOverrides[routeTemplate] = {
          path: routeTemplate,
          remoteModule: undefined // navigating to the local app shouldn't use the remote module
        };
      } else {
        localRouteOverrides[routeTemplate] = {
          path: routeTemplate,
          remoteModule: ungatedRemote.remote
        };
      }
    }
  }
  return {
    remoteModules,
    localRouteOverrides
  };
}